import type { Book } from '@wellread/api';
import { count, plural } from '@wellread/core';

// What the analysis is doing, in the same words on a cover, on the shelf and
// on the book's own page.
const stageName: Record<string, string> = {
  uploading: 'Reading the file',
  parsing: 'Reading the file',
  awaiting_confirm: 'Ready to analyse',
  unanalysed: 'Not analysed',
  extracting: 'Pulling out the ideas',
  verifying: 'Checking every quote',
  linking: 'Linking and writing questions',
  paused: 'Paused',
  failed: 'Stopped',
};

const stepOf: Partial<Record<Book['status'], Book['progress'][number]['step']>> = {
  parsing: 'parse',
  extracting: 'extract',
  verifying: 'verify',
  linking: 'link',
};

export const isReady = (b: Book) => b.status === 'ready';
/** Kept only to read: read into chapters and never analysed, until the reader asks. */
export const isUnanalysed = (b: Pick<Book, 'status'>) => b.status === 'unanalysed';
export const isTech = (b: Book) => b.track === 'technical';

/**
 * Ideas a review across every book would ask now: each book's due, added
 * up. A book no track reviews, a novel or one kept only to read, has none.
 */
export const dueAcross = (books: readonly Pick<Book, 'due'>[]) => books.reduce((n, b) => n + b.due, 0);

/** How far the analysis has got, 0–100: each of the four passes is a quarter. */
export function buildPercent(b: Book): number {
  if (b.status === 'ready') return 100;
  const order = ['parse', 'extract', 'verify', 'link'] as const;
  const step = stepOf[b.status];
  if (b.status === 'awaiting_confirm') return 25;
  if (!step) return 0;
  const i = order.indexOf(step);
  const p = (b.progress ?? []).find((x) => x.step === step);
  const frac = p && p.total > 0 ? p.done / p.total : 0;
  return Math.round(((i + frac) / 4) * 100);
}

/**
 * How far the file has been read, 0–100, when the book says: the parse
 * step's share done. Undefined until it does, and a bar then runs without a
 * figure.
 */
export function parsePercent(b: Pick<Book, 'progress'>): number | undefined {
  const p = (b.progress ?? []).find((x) => x.step === 'parse');
  return p && p.total > 0 ? Math.round((p.done / p.total) * 100) : undefined;
}

/** The stage a book's analysis is at. A novel isn't asked about, so its last pass only links the story. */
export const statusLabel = (b: Pick<Book, 'status' | 'track'>) =>
  b.status === 'linking' && b.track === 'narrative' ? 'Linking the story' : (stageName[b.status] ?? b.status);

/** The four passes of an analysis, in order, as a list of steps names them. */
export function passNames(track: Book['track']): readonly [string, string, string, string] {
  return [
    stageName.parsing!,
    stageName.extracting!,
    stageName.verifying!,
    statusLabel({ status: 'linking', track }),
  ];
}

/** The cover veil while a book is being analysed, or undefined once it's ready or kept only to read. */
export function ingestVeil(b: Book): { label: string; progress: number } | undefined {
  if (b.status === 'ready' || isUnanalysed(b)) return undefined;
  return { label: statusLabel(b), progress: buildPercent(b) };
}

/** True once the file has been read into chapters: from then on the book can be opened and read. */
export const canRead = (b: Pick<Book, 'status' | 'chapters'>) =>
  b.chapters > 0 && b.status !== 'uploading' && b.status !== 'parsing';

/** The shelf's status line under a book. */
export function shelfLine(b: Book): string {
  if (b.status !== 'ready') {
    if (b.status === 'awaiting_confirm') return 'Waiting for its track';
    if (isUnanalysed(b)) return b.position ? `Not analysed · ${b.read_percent}% read` : 'Not analysed';
    if (b.status === 'failed') return b.status_detail || 'The analysis stopped';
    const p = (b.progress ?? []).find((x) => x.step === stepOf[b.status]);
    return p && p.total ? `${statusLabel(b)} · ${count(p.done)} of ${count(p.total)}` : statusLabel(b);
  }
  if (b.track === 'narrative') {
    const p = b.position;
    const at = !p
      ? plural(b.numbered || b.chapters, 'chapter')
      : p.chapter_number
        ? `ch.${p.chapter_number} of ${b.numbered}`
        : p.chapter_title || `ch.${p.chapter_idx + 1}`;
    return `${at} · ${plural(b.notes, 'note')}`;
  }
  if (!b.concepts) return 'No concepts yet';
  return `${b.held_percent}% held · ${count(b.left)} left`;
}

/** Where "Read" goes: your place, or where the book's reading starts (past its cover and title pages). */
export function continueTarget(b: Book) {
  return {
    to: '/books/$bookId/read/$chapter' as const,
    params: { bookId: b.id, chapter: b.position?.chapter_idx ?? b.start_chapter },
    search: b.position?.char_offset ? { at: b.position.char_offset } : {},
  };
}

/**
 * The book you're in the middle of, optionally on one track: the most
 * recently read of the books that are ready or kept only to read.
 */
export function currentBook(books: Book[], track?: Book['track']): Book | undefined {
  const ready = books.filter((b) => (isReady(b) || isUnanalysed(b)) && (!track || b.track === track));
  const withPos = ready
    .filter((b) => b.position)
    .sort((a, b) => (b.position!.updated_at > a.position!.updated_at ? 1 : -1));
  return withPos[0] ?? ready[0];
}
